/*
  Hero carousel (sections/hero-carousel.liquid) — Nike-style technique, zero dependencies.
  Clone-based infinite loop: do NOT replace with a slider library or rewrite the mechanics.
  Loaded with `defer`, so the DOM is parsed before this runs.
  User-facing strings come from data attributes on the section root (set from locales/ in Liquid).
*/
(() => {
  // Inline SVGs instead of text glyphs (❚❚/▶): the webfont lacks those code
  // points, so mobile fallback fonts rendered them off-center in the ring.
  // Keep ICON_PAUSE in sync with the initial icon in sections/hero-carousel.liquid.
  const ICON_PAUSE = '<svg viewBox="0 0 9 14" width="9" height="14" aria-hidden="true"><path d="M1.5 0v14M7.5 0v14" stroke="currentColor" stroke-width="3" stroke-linecap="round" vector-effect="non-scaling-stroke"/></svg>';
  const ICON_PLAY = '<svg viewBox="0 0 9 14" width="9" height="14" aria-hidden="true"><path d="M0 0l9 7-9 7z" fill="currentColor"/></svg>';

  document.querySelectorAll('[data-hero-carousel]').forEach(initHeroCarousel);

  // Re-init on theme editor section reload; pause + jump on block select
  document.addEventListener('shopify:section:load', (e) => {
    const el = e.target.querySelector('[data-hero-carousel]');
    if (el) initHeroCarousel(el);
  });

  function initHeroCarousel(root) {
    // The script tag is rendered per section instance; guard against double init.
    if (root.dataset.heroCarouselInit) return;
    root.dataset.heroCarouselInit = 'true';

    const labels = {
      pause: root.dataset.labelPause || 'Pause automatic slide rotation',
      play: root.dataset.labelPlay || 'Resume automatic slide rotation',
      slideOf: root.dataset.labelSlideOf || 'Slide %i of %c',
      goToSlide: root.dataset.labelGoToSlide || 'Go to slide %i of %c',
    };
    const format = (label, i, c) => label.replace('%i', i).replace('%c', c);

    const track = root.querySelector('[data-track]');
    const realSlides = Array.from(track.children).filter(el => !el.dataset.clone);
    const count = realSlides.length;
    if (count < 2) return;

    const pauseBtn = root.querySelector('[data-pause]');
    const pauseIcon = root.querySelector('[data-pause-icon]');
    const prevBtn = root.querySelector('[data-prev]');
    const nextBtn = root.querySelector('[data-next]');
    const dotsWrap = root.querySelector('[data-dots]');
    const announcer = root.querySelector('[data-announcer]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplayEnabled = !!pauseBtn && !reduceMotion;

    // Clone edge slides for the seamless loop
    const firstClone = realSlides[0].cloneNode(true);
    const lastClone = realSlides[count - 1].cloneNode(true);
    [firstClone, lastClone].forEach(c => {
      c.setAttribute('aria-hidden', 'true');
      c.dataset.clone = 'true';
      c.removeAttribute('data-shopify-editor-block');
      c.querySelectorAll('video').forEach(v => { v.removeAttribute('autoplay'); v.pause(); });
    });
    track.appendChild(firstClone);
    track.insertBefore(lastClone, realSlides[0]);

    let index = 1, playing = autoplayEnabled, animating = false, timer = null, raf = null, videoCleanup = null;

    const realIndex = () => (index - 1 + count) % count;
    const currentDelay = () => Number(track.children[index]?.dataset.delay || 6000);

    function goTo(i, animate = true) {
      track.classList.toggle('no-transition', !animate);
      track.style.transform = `translateX(-${i * 100}%)`;
      if (!animate) void track.offsetWidth;
      index = i;
      animating = animate;
      updateA11y();
      syncVideos();
      if (!animate) animating = false;
    }

    track.addEventListener('transitionend', (e) => {
      if (e.target !== track) return;
      animating = false;
      if (index === count + 1) goTo(1, false);
      if (index === 0) goTo(count, false);
      startTimer();
    });

    function updateA11y() {
      Array.from(track.children).forEach((slide, i) => {
        const active = i === index;
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelectorAll('a,button').forEach(el => (el.tabIndex = active ? 0 : -1));
      });
      if (dotsWrap) dotsWrap.querySelectorAll('button').forEach((d, i) =>
        d.setAttribute('aria-current', String(i === realIndex())));
      if (announcer) announcer.textContent = format(labels.slideOf, realIndex() + 1, count);
    }

    // Play only the visible slide's video; pause the rest (saves battery, avoids audio-policy churn)
    function syncVideos() {
      Array.from(track.children).forEach((slide, i) => {
        slide.querySelectorAll('video').forEach(v => {
          if (i === index) { v.play().catch(() => {}); } else { v.pause(); }
        });
      });
    }

    function startTimer() {
      stopTimer();
      if (!playing) return;
      const slide = track.children[index];
      const video = slide && slide.querySelector('video');
      if (video) {
        startVideoTimer(video);
        return;
      }
      const delay = currentDelay();
      const started = performance.now();
      (function tick(now) {
        const p = Math.min((now - started) / delay, 1);
        pauseBtn && pauseBtn.style.setProperty('--progress', p);
        if (p < 1) raf = requestAnimationFrame(tick);
      })(started);
      timer = setTimeout(() => goTo(index + 1), delay);
    }

    // Video slides advance when the video finishes instead of after the fixed
    // delay. The media snippet renders videos with `loop`, so disable it while
    // rotation runs and restore it when rotation stops (pause/hover/focus),
    // where the video should keep looping visually. Until the video actually
    // plays (lazy-loaded sources, blocked autoplay), the configured delay acts
    // as a fallback so the carousel can't stall on a broken video.
    function startVideoTimer(video) {
      video.loop = false;
      timer = setTimeout(() => goTo(index + 1), currentDelay());
      const onEnded = () => goTo(index + 1);
      video.addEventListener('ended', onEnded);
      (function tick() {
        if (video.currentTime > 0.1 && timer) { clearTimeout(timer); timer = null; }
        if (video.duration) {
          pauseBtn && pauseBtn.style.setProperty('--progress', Math.min(video.currentTime / video.duration, 1));
        }
        raf = requestAnimationFrame(tick);
      })();
      videoCleanup = () => {
        video.removeEventListener('ended', onEnded);
        video.loop = true;
      };
    }

    function stopTimer() {
      clearTimeout(timer);
      timer = null;
      cancelAnimationFrame(raf);
      if (videoCleanup) { videoCleanup(); videoCleanup = null; }
      pauseBtn && pauseBtn.style.setProperty('--progress', 0);
    }

    prevBtn && prevBtn.addEventListener('click', () => { if (!animating) { stopTimer(); goTo(index - 1); } });
    nextBtn && nextBtn.addEventListener('click', () => { if (!animating) { stopTimer(); goTo(index + 1); } });

    pauseBtn && pauseBtn.addEventListener('click', () => {
      playing = !playing;
      pauseIcon.innerHTML = playing ? ICON_PAUSE : ICON_PLAY;
      pauseBtn.setAttribute('aria-label', playing ? labels.pause : labels.play);
      // Explicit pause also freezes the active video (visible feedback);
      // hover/focus stops keep it playing, matching Nike's behavior.
      const video = track.children[index] && track.children[index].querySelector('video');
      if (playing) {
        video && video.play().catch(() => {});
        startTimer();
      } else {
        stopTimer();
        video && video.pause();
      }
    });

    // No pause-on-hover (by request); keyboard focus still suspends rotation
    // so tabbing users aren't navigating a moving target. The pause button
    // remains the explicit stop control (WCAG 2.2.2).
    root.addEventListener('focusin', stopTimer);
    root.addEventListener('focusout', () => playing && !animating && startTimer());

    // Touch swipe (Nike parity): finger-tracked drag with axis locking so
    // vertical page scrolling is unaffected (touch-action: pan-y in CSS);
    // releasing past the threshold snaps to the adjacent slide.
    let dragX = 0, dragAxis = null, dragStartX = 0, dragStartY = 0;

    track.addEventListener('touchstart', (e) => {
      if (animating) return;
      dragAxis = null;
      dragX = 0;
      dragStartX = e.touches[0].clientX;
      dragStartY = e.touches[0].clientY;
      stopTimer();
    }, { passive: true });

    track.addEventListener('touchmove', (e) => {
      if (animating || e.touches.length !== 1) return;
      const dx = e.touches[0].clientX - dragStartX;
      const dy = e.touches[0].clientY - dragStartY;
      if (!dragAxis) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        dragAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (dragAxis !== 'x') return;
      e.preventDefault();
      dragX = dx;
      track.classList.add('no-transition');
      track.style.transform = `translateX(calc(${-index * 100}% + ${dragX}px))`;
    }, { passive: false });

    const endDrag = () => {
      const wasHorizontal = dragAxis === 'x';
      dragAxis = null;
      if (!wasHorizontal) {
        playing && !animating && startTimer();
        return;
      }
      const threshold = Math.min(80, track.offsetWidth * 0.15);
      track.classList.remove('no-transition');
      if (dragX <= -threshold) goTo(index + 1);
      else if (dragX >= threshold) goTo(index - 1);
      else if (dragX !== 0) goTo(index);
      else playing && startTimer();
      dragX = 0;
    };
    track.addEventListener('touchend', endDrag);
    track.addEventListener('touchcancel', endDrag);

    if (dotsWrap) {
      dotsWrap.innerHTML = '';
      for (let i = 0; i < count; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('aria-label', format(labels.goToSlide, i + 1, count));
        b.addEventListener('click', () => { stopTimer(); goTo(i + 1); });
        dotsWrap.appendChild(b);
      }
    }

    // Theme editor: selecting a block jumps to it and suspends autoplay
    document.addEventListener('shopify:block:select', (e) => {
      const i = realSlides.indexOf(e.target);
      if (i > -1) { playing = false; stopTimer(); goTo(i + 1); }
    });

    goTo(1, false);
    if (playing) startTimer();
    if (pauseIcon) pauseIcon.innerHTML = playing ? ICON_PAUSE : ICON_PLAY;
  }
})();